import React, { useCallback, useMemo, useState } from "react";
import { useModal } from "react-modal-hook";
import {
  useWorkflowCollaboratorsQuery,
  useWorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { CommonModal } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import { Collaborator, PermissionOption, ShareForm } from "../Common/ShareForm";

export type ShareableEntityType = "workflow" | "workflowStep";

export interface ShareWorkflowModalProps {
  entityType: ShareableEntityType;
  entityId: string;
  entityName: string;
  onClose: () => void;
  onCopyLink?: () => void;
  onInvite?: (collaborators: Collaborator[]) => void;
  onRemove?: (collaboratorId: string) => void;
}

const WORKFLOW_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "can_create", label: "Can create" },
  { value: "can_view", label: "Can view" },
];

const getModalTitle = (entityType: ShareableEntityType): string => {
  switch (entityType) {
    case "workflow":
      return "Share workflow";
    case "workflowStep":
      return "Share step";
    default:
      return "Share";
  }
};

const getInfoText = (entityType: ShareableEntityType) => {
  const entityLabel = entityType === "workflow" ? "workflow" : "step";
  return {
    text: `All Forerunner team members can view this ${entityLabel}.`,
    linkText: "Add a team member",
    linkHref: "/settings/users/team",
  };
};

const mapPolicyToPermission = (policy: string): string => {
  switch (policy) {
    case "createResource":
      return "can_create";
    case "viewResource":
      return "can_view";
    default:
      return "can_view";
  }
};

export const ShareWorkflowModalContent = ({
  entityType,
  entityId,
  entityName,
  onClose,
  onCopyLink,
  onRemove,
}: Omit<ShareWorkflowModalProps, "onInvite">) => {
  const { addSuccessToast } = useStatusToasts();

  const { data: workflowData } = useWorkflowCollaboratorsQuery({
    variables: { workflowId: entityId },
    skip: entityType !== "workflow",
  });

  const { data: stepData } = useWorkflowStepCollaboratorsQuery({
    variables: { workflowStepId: entityId },
    skip: entityType !== "workflowStep",
  });

  const initialCollaborators = useMemo<Collaborator[]>(() => {
    const collaborators =
      entityType === "workflow" ?
        workflowData?.workflowCollaborators
      : stepData?.workflowStepCollaborators;

    if (!collaborators) return [];

    return collaborators.map(collab => {
      const user = collab.user ?? collab.publicUser;
      const name =
        user ? `${user.firstName} ${user.lastName}`.trim() : "Unknown";
      const description = user?.email;

      return {
        id: collab.id,
        name: name || description || "Unknown",
        description,
        permission: mapPolicyToPermission(collab.policy),
      };
    });
  }, [
    entityType,
    workflowData?.workflowCollaborators,
    stepData?.workflowStepCollaborators,
  ]);

  const [, setCollaborators] = useState<Collaborator[]>(initialCollaborators);

  const handleCopyLink = useCallback(() => {
    if (onCopyLink) {
      onCopyLink();
    } else {
      void navigator.clipboard.writeText(window.location.href);
      addSuccessToast("Link copied to clipboard");
    }
  }, [onCopyLink, addSuccessToast]);

  const handleRemove = useCallback(
    (collaboratorId: string) => {
      setCollaborators(prev => prev.filter(c => c.id !== collaboratorId));
      onRemove?.(collaboratorId);
    },
    [onRemove],
  );

  return (
    <CommonModal
      title={getModalTitle(entityType)}
      subtitle={entityName}
      size="large"
      onCancel={onClose}
      primaryButtonText="Done"
      onSubmit={onClose}
      secondaryButtons={[
        {
          text: "Copy link",
          onClick: handleCopyLink,
        },
      ]}
      content={
        <ShareForm
          searchPlaceholder="Add email address"
          sectionLabel="Who has access"
          infoText={getInfoText(entityType)}
          permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
          initialCollaborators={initialCollaborators}
          onStateChange={setCollaborators}
          buttonText="Invite"
          showRemoveOption={true}
          onRemove={handleRemove}
        />
      }
    />
  );
};

export const useShareWorkflowModal = ({
  entityType,
  entityId,
  entityName,
  onCopyLink,
  onInvite,
  onRemove,
}: Omit<ShareWorkflowModalProps, "onClose">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowModalContent
          entityType={entityType}
          entityId={entityId}
          entityName={entityName}
          onClose={hideModal}
          onCopyLink={onCopyLink}
          onRemove={onRemove}
        />
      </Modal>
    ),
    [entityType, entityId, entityName, onCopyLink, onInvite, onRemove],
  );

  return [showModal, hideModal] as const;
};
